import apiHandle from './apiHandle';

/**
 * Chat Service - All REST API calls for chat functionality
 */
const chatService = {
  // ==================== CONVERSATIONS ====================

  /**
   * Get all conversations for the current user
   * @param {number} page - Page number (default: 1)
   * @param {number} limit - Items per page (default: 20)
   * @param {string} status - Conversation status: 'active' | 'archived' (default: 'active')
   * @returns {Promise} - API response
   */
  getConversations: (page = 1, limit = 20, status = 'active') =>
    apiHandle('/chat/conversations', 'GET', null, {}, { page, limit, status }),

  /**
   * Start or get a direct conversation with another user
   * @param {string} recipientId - User ID to start conversation with
   * @returns {Promise} - API response with conversation
   */
  startDirectConversation: (recipientId) =>
    apiHandle('/chat/conversations/direct', 'POST', { recipientId }),

  /**
   * Create a group conversation
   * @param {Array} participantIds - Array of user IDs
   * @param {string} groupName - Name of the group
   * @param {string} groupImage - URL of group image (optional)
   * @returns {Promise} - API response with conversation
   */
  createGroupConversation: (participantIds, groupName, groupImage = null) =>
    apiHandle('/chat/conversations/group', 'POST', { 
      participantIds, 
      groupName, 
      groupImage 
    }),

  /**
   * Get or create a booking-related conversation
   * @param {string} bookingId - Booking ID
   * @returns {Promise} - API response with conversation
   */
  getBookingConversation: (bookingId) =>
    apiHandle('/chat/conversations/booking', 'POST', { bookingId }),

  /**
   * Get a single conversation by ID
   * @param {string} conversationId - Conversation ID
   * @returns {Promise} - API response with conversation details
   */
  getConversation: (conversationId) =>
    apiHandle(`/chat/conversations/${conversationId}`, 'GET'),

  /**
   * Archive a conversation
   * @param {string} conversationId - Conversation ID
   * @returns {Promise} - API response
   */
  archiveConversation: (conversationId) =>
    apiHandle(`/chat/conversations/${conversationId}/archive`, 'PATCH'),

  /**
   * Delete a conversation
   * @param {string} conversationId - Conversation ID
   * @returns {Promise} - API response
   */
  deleteConversation: (conversationId) =>
    apiHandle(`/chat/conversations/${conversationId}`, 'DELETE'),

  // ==================== MESSAGES ====================

  /**
   * Get messages for a conversation
   * @param {string} conversationId - Conversation ID
   * @param {number} limit - Number of messages to fetch (default: 50)
   * @param {string} before - Message ID for pagination (optional)
   * @returns {Promise} - API response with messages
   */
  getMessages: (conversationId, limit = 50, before = null) =>
    apiHandle(
      `/chat/conversations/${conversationId}/messages`, 
      'GET', 
      null, 
      {}, 
      { limit, ...(before && { before }) }
    ),

  /**
   * Send a message via REST API
   * @param {string} conversationId - Conversation ID
   * @param {Object} data - Message data { content, messageType, attachments, location, replyTo }
   * @returns {Promise} - API response with message
   */
  sendMessage: (conversationId, data) =>
    apiHandle(`/chat/conversations/${conversationId}/messages`, 'POST', data),

  /**
   * Mark messages as read
   * @param {string} conversationId - Conversation ID
   * @param {Array} messageIds - Array of message IDs to mark as read (optional, marks all if empty)
   * @returns {Promise} - API response
   */
  markAsRead: (conversationId, messageIds = []) =>
    apiHandle(`/chat/conversations/${conversationId}/read`, 'POST', { messageIds }),

  /**
   * Send a file message (image, video, audio, or document)
   * @param {string} conversationId - Conversation ID
   * @param {File} file - File to upload
   * @param {string} messageType - Type of message: 'image' | 'video' | 'audio' | 'document'
   * @param {string} text - Optional text caption
   * @returns {Promise} - API response with sent message
   */
  sendFileMessage: (conversationId, file, messageType = 'document', text = '') => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('messageType', messageType);
    if (text) {
      formData.append('text', text);
    }
    return apiHandle(`/chat/conversations/${conversationId}/messages`, 'POST', formData);
  },

  /**
   * Delete a message
   * @param {string} messageId - Message ID
   * @param {boolean} forEveryone - Delete for everyone (default: false)
   * @returns {Promise} - API response
   */
  deleteMessage: (messageId, forEveryone = false) =>
    apiHandle(`/chat/messages/${messageId}`, 'DELETE', null, {}, { forEveryone }),

  // ==================== UTILITIES ====================

  /**
   * Get total unread message count
   * @returns {Promise} - API response with unread count
   */
  getUnreadCount: () =>
    apiHandle('/chat/unread-count', 'GET'),

  /**
   * Search messages
   * @param {string} query - Search query
   * @param {string} conversationId - Limit search to specific conversation (optional)
   * @param {number} page - Page number (default: 1)
   * @returns {Promise} - API response with search results
   */
  searchMessages: (query, conversationId = null, page = 1) =>
    apiHandle('/chat/search', 'GET', null, {}, { 
      q: query, 
      ...(conversationId && { conversationId }), 
      page 
    }),

  // ==================== GROUP MANAGEMENT ====================

  /**
   * Add a participant to a group conversation
   * @param {string} conversationId - Conversation ID
   * @param {string} userId - User ID to add
   * @returns {Promise} - API response
   */
  addParticipant: (conversationId, userId) =>
    apiHandle(`/chat/conversations/${conversationId}/participants`, 'POST', { userId }),

  /**
   * Remove a participant from a group conversation
   * @param {string} conversationId - Conversation ID
   * @param {string} userId - User ID to remove
   * @returns {Promise} - API response
   */
  removeParticipant: (conversationId, userId) =>
    apiHandle(`/chat/conversations/${conversationId}/participants/${userId}`, 'DELETE'),

  /**
   * Update group details
   * @param {string} conversationId - Conversation ID
   * @param {Object} data - Group data { groupName, groupImage }
   * @returns {Promise} - API response
   */
  updateGroupDetails: (conversationId, data) =>
    apiHandle(`/chat/conversations/${conversationId}/group`, 'PATCH', data),

  // ==================== USER MANAGEMENT ====================

  /**
   * Get all users that can be chatted with
   * @param {string} search - Search query (optional)
   * @param {number} page - Page number (default: 1)
   * @param {number} limit - Items per page (default: 20)
   * @returns {Promise} - API response with users
   */
  getAvailableUsers: (search = '', page = 1, limit = 20) =>
    apiHandle('/user/', 'GET', null, {}, {
      search, 
      page, 
      limit 
    }),
};

export default chatService;
